import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { fetchHospitalitySnapshot } from '../../../hospitality/infra/hospitalityRepo'
import type { DiningTable } from '../../../hospitality/domain/hospitalityTypes';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { showInfo } from '../../../shared/ui/appToast';
import { getPosFulfillmentType, setPosFulfillmentType } from '../../../shared/pos/posHandoffSettings';
import type { OperationalConfirmOptions } from '../../../shared/ui/useOperationalConfirm';

export function usePosDiningCheckout(options: {
  currentBranchId: string | null | undefined;
  hasTablesCapability: boolean;
  openCheckout: () => void;
  diningTableIdRef: MutableRefObject<string | null>;
  initialDiningTableId?: string | null;
  confirm?: (opts: OperationalConfirmOptions) => Promise<boolean>;
}) {
  const { currentBranchId, hasTablesCapability, openCheckout, diningTableIdRef, initialDiningTableId, confirm } = options;
  const [diningTablePickerVisible, setDiningTablePickerVisible] = useState(false);
  const [diningTableOptions, setDiningTableOptions] = useState<DiningTable[]>([]);
  const [diningTablePickerLoading, setDiningTablePickerLoading] = useState(false);
  const [selectedDiningTableId, setSelectedDiningTableId] = useState<string | null>(initialDiningTableId ?? null);
  const isBeginningRef = useRef(false);

  useLayoutEffect(() => {
    if (!initialDiningTableId) return;
    diningTableIdRef.current = initialDiningTableId;
    setSelectedDiningTableId((prev) => prev ?? initialDiningTableId);
    setPosFulfillmentType('dine_in');
  }, [diningTableIdRef, initialDiningTableId]);
  /** checkout = elegir mesa al cobrar; assign = cambiar mesa en el carrito. */
  const pickerIntentRef = useRef<'checkout' | 'assign'>('checkout');

  useEffect(() => {
    if (!initialDiningTableId || !currentBranchId) return;
    let cancelled = false;
    void (async () => {
      setDiningTablePickerLoading(true);
      try {
        const { diningTables } = await fetchHospitalitySnapshot(currentBranchId);
        if (cancelled) return;
        const options = diningTables.filter((t) => t.status !== 'closed');
        setDiningTableOptions(options);
        const table = options.find((t) => t.id === initialDiningTableId);
        if (table) {
          setSelectedDiningTableId(table.id);
          diningTableIdRef.current = table.id;
          setPosFulfillmentType('dine_in');
        }
      } catch (error) {
        // falla silenciosa: el picker sigue disponible manualmente
        captureCatch('pos', 'dining-snapshot-prefetch')(error);
      } finally {
        if (!cancelled) setDiningTablePickerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialDiningTableId, currentBranchId, diningTableIdRef]);

  const selectedDiningTable = useMemo(
    () => diningTableOptions.find((t) => t.id === selectedDiningTableId) ?? null,
    [diningTableOptions, selectedDiningTableId]
  );

  const proceedWithoutTableAndCheckout = useCallback(() => {
    setSelectedDiningTableId(null);
    diningTableIdRef.current = null;
    openCheckout();
  }, [diningTableIdRef, openCheckout]);

  const applyDiningTableSelection = useCallback(
    (tableId: string | null) => {
      if (tableId === null) {
        setSelectedDiningTableId(null);
        diningTableIdRef.current = null;
        return;
      }
      setSelectedDiningTableId(tableId);
      diningTableIdRef.current = tableId;
      setPosFulfillmentType('dine_in');
    },
    [diningTableIdRef],
  );

  const clearDiningTableSelection = useCallback(() => {
    setSelectedDiningTableId(null);
    diningTableIdRef.current = null;
  }, [diningTableIdRef]);

  const loadDiningTableOptions = useCallback(async () => {
    if (!currentBranchId) return;
    setDiningTablePickerLoading(true);
    try {
      const { diningTables } = await fetchHospitalitySnapshot(currentBranchId);
      setDiningTableOptions(diningTables.filter((t) => t.status !== 'closed'));
    } catch (error) {
      captureCatch('pos', 'dining-snapshot-picker')(error);
      showInfo('Mesas no disponibles', 'No pudimos cargar el listado.');
    } finally {
      setDiningTablePickerLoading(false);
    }
  }, [currentBranchId]);

  const openChangeDiningTablePicker = useCallback(() => {
    if (!hasTablesCapability || !currentBranchId) return;
    pickerIntentRef.current = 'assign';
    setDiningTablePickerVisible(true);
    void loadDiningTableOptions();
  }, [currentBranchId, hasTablesCapability, loadDiningTableOptions]);

  const beginCheckout = useCallback(async () => {
    if (isBeginningRef.current) return;
    isBeginningRef.current = true;
    pickerIntentRef.current = 'checkout';
    try {
      // Cuenta ya abierta desde Mesas / mesa ya elegida: no limpiar ni abrir picker.
      const already = selectedDiningTableId ?? initialDiningTableId ?? null;
      if (already) {
        setSelectedDiningTableId(already);
        diningTableIdRef.current = already;
        setPosFulfillmentType('dine_in');
        openCheckout();
        return;
      }

      diningTableIdRef.current = null;
      // Para llevar no requiere mesa: el picker solo aplica a Comer acá.
      if (!hasTablesCapability || !currentBranchId || getPosFulfillmentType() !== 'dine_in') {
        proceedWithoutTableAndCheckout();
        return;
      }
      setDiningTablePickerVisible(true);
      await loadDiningTableOptions();
    } catch (error) {
      captureCatch('pos', 'dining-snapshot-begin-checkout')(error);
      showInfo('Mesas no disponibles', 'Seguimos el cobro sin mesa.');
      proceedWithoutTableAndCheckout();
    } finally {
      setDiningTablePickerLoading(false);
      isBeginningRef.current = false;
    }
  }, [
    currentBranchId,
    diningTableIdRef,
    hasTablesCapability,
    initialDiningTableId,
    openCheckout,
    proceedWithoutTableAndCheckout,
    selectedDiningTableId,
    loadDiningTableOptions,
  ]);

  const confirmDiningTableSelection = useCallback(
    async (tableId: string | null) => {
      if (tableId === null) {
        applyDiningTableSelection(null);
        setDiningTablePickerVisible(false);
        if (pickerIntentRef.current === 'checkout') {
          openCheckout();
        }
        return;
      }
      const table = diningTableOptions.find((t) => t.id === tableId);
      if (table?.status === 'occupied' && tableId !== selectedDiningTableId) {
        return;
      }
      if (table?.status === 'reserved') {
        if (confirm) {
          const ok = await confirm({
            title: 'Mesa reservada',
            message: `¿Querés usar ${table.name} igual?`,
            confirmLabel: 'Usar',
          });
          if (!ok) return;
        }
      }
      applyDiningTableSelection(tableId);
      setDiningTablePickerVisible(false);
      if (pickerIntentRef.current === 'checkout') {
        openCheckout();
      }
    },
    [
      applyDiningTableSelection,
      confirm,
      diningTableOptions,
      openCheckout,
      selectedDiningTableId,
    ],
  );

  const confirmDiningTableAndCheckout = confirmDiningTableSelection;

  return {
    diningTablePickerVisible,
    setDiningTablePickerVisible,
    diningTableOptions,
    diningTablePickerLoading,
    selectedDiningTable,
    selectedDiningTableId,
    beginCheckout,
    confirmDiningTableAndCheckout,
    confirmDiningTableSelection,
    clearDiningTableSelection,
    openChangeDiningTablePicker,
  };
}
